/**
 * investigationStore — the OBSERVE → UNDERSTAND → ACT → EVOLVE thread.
 *
 * Brain Intelligence pages were architecturally a constellation of 30
 * independent dashboards. The "flow" was visual only (RelatedPagesGrid
 * links) — clicking a related page started fresh with no context.
 *
 * This store is the **investigation thread**: a single user-driven
 * journey through the brain phases. Every Brain page can:
 *
 *   - READ the current thread to know what the user is investigating
 *     (e.g. "Asset F67423 has health=50%; sepsis-style root cause is
 *     'bearing wear'; user is now hunting for a corrective action").
 *
 *   - APPEND a step when the user takes an action ("ran physics
 *     simulation, got 87% convergence on bearing-wear hypothesis").
 *
 *   - BRANCH a step when the user pursues an alternate path
 *     ("retry with looser tolerance" → new sub-thread).
 *
 *   - CLEAR / NEW THREAD at the end of an investigation so the next
 *     one starts clean.
 *
 * Persistence: the active thread is mirrored into sessionStorage so a
 * page reload keeps the user in context. Threads are NOT shared
 * across tabs (deliberate — each tab is a separate investigation).
 *
 * Telemetry: every append is published to the BrainTelemetry stream so
 * we can compute the "cross-phase depth" KPI (avg phases per
 * investigation) — a key product metric for "are people actually
 * using the OBSERVE→UNDERSTAND→ACT→EVOLVE flow?"
 */
import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'

export type BrainPhase = 'observe' | 'understand' | 'act' | 'evolve'

export interface InvestigationStep {
  /** Stable client-generated id for the step (uuid-like). */
  id: string
  /** Phase the step belongs to. */
  phase: BrainPhase
  /** Brain page path (`/brain/cognitive-twin`, `/brain/causal`, …). */
  pagePath: string
  /** Human-readable page label (taken from brainNavigation config). */
  pageLabel: string
  /** What this step contributed (free-form, set by the page itself). */
  finding: string
  /** Optional payload the page enriches the step with. */
  context?: {
    assetId?: string
    industryId?: string
    anomalyId?: string
    hypothesisId?: string
    decisionId?: string
    rootCause?: string
    physicsModel?: string
    confidence?: number
    [k: string]: unknown
  }
  /** Captured at append time. */
  createdAt: string
}

/**
 * An Artifact is an output of a playground card that the user considered
 * "worth keeping" — pinned twin trials, validated hypotheses, physics
 * violations, experiment results, oracle citations. Artifacts are the
 * raw material the Report Composer (Phase D.8) uses to auto-generate
 * the final analysis report.
 */
export interface InvestigationArtifact {
  id: string
  /** Step id that produced this artifact. */
  stepId: string
  /** Kind drives how the Report Composer renders it. */
  kind:
    | 'twin_trial'
    | 'validated_hypothesis'
    | 'physics_residual'
    | 'experiment_result'
    | 'oracle_citation'
    | 'causal_edge'
    | 'annotation'
    | 'other'
  /** Short human label. */
  label: string
  /** The output payload (free shape — Report Composer knows each kind). */
  data: unknown
  /** When true, flagged for SOP promotion on investigation close. */
  worthPromoting?: boolean
  pinnedBy?: string
  pinnedAt: string
}

/** Investigation status lifecycle. */
export type InvestigationStatus = 'open' | 'resolved' | 'abandoned'

export interface InvestigationThread {
  id: string
  /** Optional one-line title set when the thread starts. */
  title?: string
  /** Free-text investigation goal — what the user is trying to learn. */
  goal?: string
  /** Source asset/industry the investigation is anchored to. */
  anchor: {
    industryId?: string
    assetId?: string
    triggeredBy?: string  // e.g. "anomaly_alert", "user_query", "scheduled_review"
  }
  steps: InvestigationStep[]
  /** Pinned outputs the user wants preserved — seeds the Report Composer. */
  artifacts: InvestigationArtifact[]
  /** Lifecycle status. `closed` (legacy) is retained for backwards-compat. */
  status: InvestigationStatus
  /** User-editable discussion section, shown in the notebook view. */
  discussion?: string
  /** User-editable recommendation section, shown in the notebook view. */
  recommendation?: string
  /** Peer reviewers who have signed off on this investigation. */
  reviewers?: string[]
  /** True when the report was pushed for SOP promotion. */
  sopPromoted?: boolean
  /** True when user marked the thread complete (action taken / archived). */
  closed: boolean
  startedAt: string
  closedAt?: string
}

export interface InvestigationState {
  active: InvestigationThread | null
  history: InvestigationThread[]   // last N closed threads (max 25)
  maxHistory: number

  // mutations
  startThread: (anchor: InvestigationThread['anchor'], title?: string, goal?: string) => string
  appendStep: (step: Omit<InvestigationStep, 'id' | 'createdAt'>) => void
  closeThread: (outcome?: string) => void
  clearActive: () => void
  resumeThread: (id: string) => void
  /** D.6 additions — artifacts + discussion + recommendation + goal */
  setGoal: (goal: string) => void
  setDiscussion: (md: string) => void
  setRecommendation: (md: string) => void
  setStatus: (status: InvestigationStatus) => void
  pinArtifact: (artifact: Omit<InvestigationArtifact, 'id' | 'pinnedAt'>) => string
  unpinArtifact: (id: string) => void
  toggleArtifactWorthPromoting: (id: string) => void
  addReviewer: (username: string) => void

  // derived helpers
  phasesVisited: () => Set<BrainPhase>
  depth: () => number
  latestFinding: () => InvestigationStep | undefined
  artifactCount: () => number
  worthPromotingCount: () => number
}

function uid(): string {
  // RFC4122-like compact id; no external dep.
  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 10)
  )
}

const PHASE_RANK: Record<BrainPhase, number> = {
  observe: 0,
  understand: 1,
  act: 2,
  evolve: 3,
}

export const useInvestigationStore = create<InvestigationState>()(
  devtools(
    persist(
      (set, get) => ({
        active: null,
        history: [],
        maxHistory: 25,

        startThread: (anchor, title, goal) => {
          const id = uid()
          const thread: InvestigationThread = {
            id,
            title,
            goal,
            anchor,
            steps: [],
            artifacts: [],
            status: 'open',
            closed: false,
            startedAt: new Date().toISOString(),
          }
          set({ active: thread })
          return id
        },

        setGoal: (goal) => {
          const active = get().active
          if (!active) return
          set({ active: { ...active, goal } })
        },
        setDiscussion: (md) => {
          const active = get().active
          if (!active) return
          set({ active: { ...active, discussion: md } })
        },
        setRecommendation: (md) => {
          const active = get().active
          if (!active) return
          set({ active: { ...active, recommendation: md } })
        },
        setStatus: (status) => {
          const active = get().active
          if (!active) return
          set({ active: { ...active, status } })
        },
        pinArtifact: (artifact) => {
          const active = get().active
          if (!active) {
            // Auto-open a thread anchored to this artifact's step
            get().startThread({ triggeredBy: 'artifact_pin' })
          }
          const cur = get().active!
          const id = uid()
          const newArtifact: InvestigationArtifact = {
            ...artifact,
            id,
            pinnedAt: new Date().toISOString(),
          }
          set({
            active: {
              ...cur,
              artifacts: [...(cur.artifacts ?? []), newArtifact],
            },
          })
          return id
        },
        unpinArtifact: (id) => {
          const active = get().active
          if (!active) return
          set({
            active: {
              ...active,
              artifacts: (active.artifacts ?? []).filter((a) => a.id !== id),
            },
          })
        },
        toggleArtifactWorthPromoting: (id) => {
          const active = get().active
          if (!active) return
          set({
            active: {
              ...active,
              artifacts: (active.artifacts ?? []).map((a) =>
                a.id === id ? { ...a, worthPromoting: !a.worthPromoting } : a,
              ),
            },
          })
        },
        addReviewer: (username) => {
          const active = get().active
          if (!active) return
          const reviewers = Array.from(new Set([...(active.reviewers ?? []), username]))
          set({ active: { ...active, reviewers } })
        },

        appendStep: (step) => {
          const active = get().active
          if (!active) {
            // Auto-open a thread anchored to this step so we never lose data.
            get().startThread({
              industryId: step.context?.industryId,
              assetId: step.context?.assetId,
              triggeredBy: 'auto_open',
            })
            const fresh = get().active
            if (!fresh) return
            set({
              active: {
                ...fresh,
                steps: [
                  {
                    ...step,
                    id: uid(),
                    createdAt: new Date().toISOString(),
                  },
                ],
              },
            })
            return
          }
          // v28: dedup consecutive entries on the same page. The breadcrumb
          // was polluting itself when users hopped Twin↔Physics↔Twin (each
          // PromoteButton click appends), turning the trail into
          // "Twin > Physics > Physics > Twin > Twin". When the new step is
          // on the same pagePath as the most recent one, update the
          // existing step (keeping the latest finding/context) instead of
          // pushing a new entry.
          const last = active.steps[active.steps.length - 1]
          if (last && last.pagePath === step.pagePath && last.phase === step.phase) {
            const merged: InvestigationStep = {
              ...last,
              finding: step.finding || last.finding,
              context: { ...(last.context || {}), ...(step.context || {}) },
              createdAt: new Date().toISOString(),
            }
            set({
              active: {
                ...active,
                steps: [...active.steps.slice(0, -1), merged],
              },
            })
            return
          }
          set({
            active: {
              ...active,
              steps: [
                ...active.steps,
                {
                  ...step,
                  id: uid(),
                  createdAt: new Date().toISOString(),
                },
              ],
            },
          })
        },

        closeThread: (outcome) => {
          const active = get().active
          if (!active) return
          const closed: InvestigationThread = {
            ...active,
            closed: true,
            closedAt: new Date().toISOString(),
            title: outcome || active.title,
          }
          const history = [closed, ...get().history].slice(0, get().maxHistory)
          set({ active: null, history })
        },

        clearActive: () => set({ active: null }),

        resumeThread: (id) => {
          const t = get().history.find((h) => h.id === id)
          if (!t) return
          set({
            active: { ...t, closed: false, closedAt: undefined },
            history: get().history.filter((h) => h.id !== id),
          })
        },

        phasesVisited: () => {
          const a = get().active
          if (!a) return new Set<BrainPhase>()
          return new Set(a.steps.map((s) => s.phase))
        },

        depth: () => get().active?.steps.length ?? 0,

        latestFinding: () => {
          const steps = get().active?.steps
          return steps && steps.length ? steps[steps.length - 1] : undefined
        },

        artifactCount: () => get().active?.artifacts?.length ?? 0,
        worthPromotingCount: () =>
          (get().active?.artifacts ?? []).filter((a) => a.worthPromoting).length,
      }),
      {
        name: 'brain-investigation-thread',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (s) => ({ active: s.active, history: s.history }),
      }
    ),
    { name: 'investigation-store' }
  )
)

/** Helper: rank phases for "next phase" hints. */
export function nextPhase(p: BrainPhase): BrainPhase | null {
  const order: BrainPhase[] = ['observe', 'understand', 'act', 'evolve']
  const i = order.indexOf(p)
  return i >= 0 && i < order.length - 1 ? order[i + 1] : null
}

export function phaseRank(p: BrainPhase): number {
  return PHASE_RANK[p]
}
